import { Injectable } from '@angular/core';
import type { Form } from '@nrwl/rx-models';

type ImageSource = string | number;

interface ImageSnapshot {
  id: unknown;
  sectionId?: unknown;
  source: ImageSource;
  originalSource?: ImageSource;
  signature: string;
}

/** Resolves clean customization images without replacing the patient's saved image values. */
@Injectable()
export class CustomizationImageSourceService {
  private enabled = false;
  private planImages: ImageSnapshot[] = [];
  private templateImages: ImageSnapshot[] = [];

  configure(form: Pick<Form, 'form_data' | 'template'>): void {
    this.enabled = !!form;
    this.planImages = [];
    this.templateImages = [];
    const planStructure = form?.form_data;
    const templateStructure = form?.template?.template;
    if (typeof planStructure !== 'string' || typeof templateStructure !== 'string') {
      return;
    }

    try {
      this.planImages = this.readImages(planStructure);
      this.templateImages = this.readImages(templateStructure);
    } catch (error: unknown) {
      this.planImages = [];
      this.templateImages = [];
      console.warn('Unable to load original images for plan customization.', error);
    }
  }

  /** Seed provenance on a working copy before the designer takes its pristine baseline. */
  prepareFormData(structure: string): string {
    if (!this.enabled) {
      return structure;
    }

    const data: unknown = JSON.parse(structure);
    let updated = false;
    for (const { widget, sectionId } of this.getWidgets(data)) {
      if (!this.isRecord(widget) || !this.isRecord(widget['properties'])) {
        continue;
      }
      const property = widget['properties']['value'];
      if (!this.isRecord(property)) {
        continue;
      }
      const current = this.imageSnapshot(widget);
      if (!current) {
        continue;
      }
      const source = current.originalSource ?? this.findLoadedOriginal(current, sectionId);
      if (source === undefined || property['original'] === source) {
        continue;
      }
      property['original'] = source;
      updated = true;
    }

    return updated ? JSON.stringify(data) : structure;
  }

  getDisplaySource(widget: object): ImageSource | undefined {
    const current = this.imageSnapshot(widget);
    return this.enabled ? current?.originalSource : undefined;
  }

  registerNewImage(widget: object): void {
    if (
      !this.enabled ||
      !this.isRecord(widget) ||
      widget['name'] !== 'fb-image' ||
      !this.isRecord(widget['properties']) ||
      !this.isRecord(widget['properties']['value'])
    ) {
      return;
    }
    const property = widget['properties']['value'];
    if (this.isImageSource(property['value']) && !this.isImageSource(property['original'])) {
      property['original'] = property['value'];
    }
  }

  private findLoadedOriginal(current: ImageSnapshot, sectionId: unknown): ImageSource | undefined {
    if (!this.templateImages.length) {
      return undefined;
    }

    const instances = this.planImages.filter(
      (image) =>
        image.source === current.source &&
        (current.id
          ? image.id === current.id
          : !image.id && image.signature === current.signature) &&
        (!sectionId || !image.sectionId || image.sectionId === sectionId),
    );
    const originals = instances.map((image) => this.findOriginal(image));
    const originalSource = originals[0];
    if (originalSource !== undefined && originals.every((source) => source === originalSource)) {
      return originalSource;
    }
    if (instances.length) {
      console.warn(
        'Unable to uniquely match the original image for plan customization; keeping the saved image.',
      );
    }
    return undefined;
  }

  private findOriginal(image: ImageSnapshot): ImageSource | undefined {
    // Widget IDs are regenerated on rehydration, so also match the saved field's design.
    const sameId = this.templateImages.filter((candidate) => image.id && candidate.id === image.id);
    const candidates = sameId.length
      ? sameId
      : this.templateImages.filter(
          (candidate) =>
            candidate.signature === image.signature &&
            (!image.sectionId || !candidate.sectionId || candidate.sectionId === image.sectionId),
        );
    const source = candidates[0]?.source;
    return candidates.every((candidate) => candidate.source === source) ? source : undefined;
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  private propertyValue(properties: Record<string, unknown>, key: string): unknown {
    const property = properties[key];
    return this.isRecord(property) ? property['value'] : undefined;
  }

  private isImageSource(value: unknown): value is ImageSource {
    return (
      (typeof value === 'string' && !!value.trim()) ||
      (typeof value === 'number' && Number.isFinite(value) && value > 0)
    );
  }

  private imageSnapshot(value: unknown): ImageSnapshot | null {
    if (
      !this.isRecord(value) ||
      value['name'] !== 'fb-image' ||
      !this.isRecord(value['properties'])
    ) {
      return null;
    }

    const properties = value['properties'];
    const source = this.propertyValue(properties, 'value');
    if (!this.isImageSource(source)) {
      return null;
    }
    const image = properties['value'];
    const original = this.isRecord(image) ? image['original'] : undefined;

    return {
      id: value['id'],
      source,
      originalSource: this.isImageSource(original) ? original : undefined,
      signature: JSON.stringify([
        value['x'] || 0,
        value['y'] || 0,
        this.propertyValue(properties, 'text') ?? 'Label 1',
        this.propertyValue(properties, 'content') || '',
        this.propertyValue(properties, 'hideLabel') || false,
      ]),
    };
  }

  private readImages(structure: string): ImageSnapshot[] {
    return this.getWidgets(JSON.parse(structure)).flatMap(({ widget, sectionId }) => {
      const image = this.imageSnapshot(widget);
      return image ? [{ ...image, sectionId }] : [];
    });
  }

  private getWidgets(data: unknown): { widget: unknown; sectionId: unknown }[] {
    if (!this.isRecord(data)) {
      throw new Error('Invalid image source structure.');
    }

    const sections = Array.isArray(data['sections']) ? data['sections'] : [data['section']];
    return sections.flatMap((section: unknown) => {
      if (!this.isRecord(section) || !Array.isArray(section['widgets'])) {
        return [];
      }

      return section['widgets'].map((widget: unknown) => ({ widget, sectionId: section['id'] }));
    });
  }
}
